import axios from "axios";
import { Photo } from "../Contract/Photos";
import { getAuthHeaders } from "./authSession";

type CreatePhotoUploadRequest = {
  name?: string;
  description?: string;
  date?: string; // yyyy-mm-dd (optional)
  contentType: string; // e.g. image/jpeg
};

type ReplacePhotoUploadRequest = {
  imageId: string;
  name?: string;
  description?: string;
  date?: string;
  contentType: string;
};

type UploadInitResponse = {
  imageId: string;
  uploadUrl: string;
  readUrl: string;
  objectKey: string;
};

type Rejectable = { status: number; message: string; raw?: string };

export class PhotosApi {
  private baseUrl: string = process.env.REACT_APP_API_ENDPOINT || "";
  private authToken?: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_ENDPOINT || "";
    this.authToken = undefined;
  }

  setAuthToken(token?: string) {
    this.authToken = token;
  }

  private async post<T>(payload: unknown): Promise<T> {
    const res = await fetch(this.baseUrl, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...getAuthHeaders(),
        ...(this.authToken ? { Authorization: this.authToken } : {}),
      },
      body: JSON.stringify(payload),
    });

    const text = await res.text();
    if (!res.ok) {
      throw {
        status: res.status,
        message: "Request failed",
        raw: text,
      } satisfies Rejectable;
    }

    if (!text) return {} as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      // In case lambda returns plain text (shouldn't), surface it.
      return { raw: text } as unknown as T;
    }
  }

  async getPhotos(): Promise<Photo[]> {
    return this.post<Photo[]>({ action: "getPhotos" });
  }

  async deletePhoto(
    imageId: string,
  ): Promise<{ ok: boolean; deleted: boolean }> {
    return this.post<{ ok: boolean; deleted: boolean }>({
      action: "deletePhoto",
      imageId,
    });
  }

  async createAndUploadPhoto(
    file: File,
    meta: Omit<CreatePhotoUploadRequest, "contentType">,
  ): Promise<UploadInitResponse> {
    const init = await this.post<UploadInitResponse>({
      action: "createPhotoUpload",
      ...meta,
      contentType: file.type,
    });

    await this.uploadToPresignedUrl(init.uploadUrl, file, file.type);
    return init;
  }

  async replaceAndUploadPhoto(
    imageId: string,
    file: File,
    meta?: Omit<ReplacePhotoUploadRequest, "imageId" | "contentType">,
  ): Promise<UploadInitResponse> {
    const init = await this.post<UploadInitResponse>({
      action: "replacePhotoUpload",
      imageId,
      ...(meta ?? {}),
      contentType: file.type,
    });

    await this.uploadToPresignedUrl(init.uploadUrl, file, file.type);
    return init;
  }

  /**
   * Uploads the file bytes to the presigned PUT URL.
   * IMPORTANT: must match the Content-Type used when signing in Lambda.
   */
  private async uploadToPresignedUrl(
    url: string,
    file: Blob,
    contentType: string,
  ) {
    const res = await fetch(url, {
      method: "PUT",
      headers: {
        "content-type": contentType,
      },
      body: file,
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw {
        status: res.status,
        message: "S3 upload failed",
        raw: text,
      } satisfies Rejectable;
    }
  }
}
